import React from 'react';
import { useSpring, animated, config } from 'react-spring';
import SEO from '../../components/SEO';

const TermsOfService = () => {
    // Colors for consistent theming
    const primaryTextColor = "text-[#212529]";
    const secondaryTextColor = "text-[#212529]/80";
    const tertiaryTextColor = "text-[#212529]/60";

    // Animation for content entrance
    const contentAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(20px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
        config: { ...config.gentle, tension: 120, friction: 14 },
        delay: 100
    });

    return (
        <div className="min-h-screen font-sans overflow-hidden relative">
            <SEO
                title="Terms of Service - Axialis"
                description="Terms of service for the Axialis website."
                path="/terms"
            />
            <div className="container mx-auto px-4 py-32">
                <animated.div style={contentAnimation} className="max-w-4xl mx-auto backdrop-blur-sm bg-white/40 p-8 md:p-12 rounded-xl shadow-xl border border-white/40">
                    <h1 className={`text-4xl font-display font-bold mb-6 ${primaryTextColor}`}>Terms of Service</h1>
                    <div className={`h-0.5 w-16 bg-primary mb-8`}></div>
                    <p className={`${secondaryTextColor} mb-8`}>Last Updated: {new Date().toLocaleDateString()}</p>

                    <div className="space-y-8">
                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Introduction</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>Welcome to Axialis. These terms outline the simple rules for using our website.</p>
                                <p>By using this website, you agree to these terms. If you don't agree with any part of these terms, please don't use our website.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Website Usage</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>You may use our website for lawful purposes only. When using our website, you agree not to:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Use the website for any illegal activities</li>
                                    <li>Attempt to gain unauthorized access to our systems</li>
                                    <li>Interfere with the website's functionality</li>
                                    <li>Copy, reproduce, or distribute our content without permission</li>
                                    <li>Use automated tools to access or scrape our website</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Content and Intellectual Property</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>All content on this website, including text, images, logos, and design elements, is owned by Axialis or our licensors and is protected by copyright and other intellectual property laws.</p>
                                <p>You may view and use the website for personal, non-commercial purposes. You may not modify, copy, distribute, or reproduce our content without written permission.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Disclaimer</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>This website is provided "as is" without any warranties, express or implied. We make no guarantees about the accuracy, completeness, or reliability of the information on our website.</p>
                                <p>We reserve the right to modify or discontinue any part of our website at any time without notice.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Limitation of Liability</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>To the fullest extent permitted by law, Axialis shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of this website.</p>
                                <p>This includes but is not limited to damages for loss of data, profit, or business interruption.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>External Links</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>Our website may contain links to external websites. We are not responsible for the content, privacy practices, or terms of service of these external sites.</p>
                                <p>We recommend reviewing the terms and privacy policies of any external websites you visit.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Changes to Terms</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>We may update these terms from time to time. Any changes will be posted on this page with an updated date.</p>
                                <p>Your continued use of the website after changes are posted constitutes acceptance of the new terms.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Contact Us</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>If you have any questions about these Terms of Service, please contact us at <a href="mailto:support@rivieratech.solutions" className="text-primary hover:underline">support@rivieratech.solutions</a></p>
                            </div>
                        </section>

                        {/* Attribution backlink */}
                        <div className="pt-8 mt-8 border-t border-[#212529]/10">
                            <p className="text-[#212529]/40 text-xs text-center">
                                This website was designed and built by{' '}
                                <a
                                    href="https://rivieratech.solutions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary/60 hover:text-primary hover:underline transition-colors duration-300"
                                >
                                    RivieraTech Solutions
                                </a>
                                {' '}&mdash; a professional website builder specializing in business websites, software development, hardware design, and electronics consulting.
                            </p>
                        </div>
                    </div>
                </animated.div>
            </div>
        </div>
    );
};

export default TermsOfService;
